import type { FC } from 'react';
import { ProductCard } from '@packages/modules';
import type { ProductCardProps } from '@packages/modules/src/ProductCard/types';
import { defineMessage, useIntl } from 'react-intl';
import { setScrollingPosition } from '@packages/shared/src/hooks/useScrollRestoration';
import { useRouter } from 'next/router';
import { mergeQueryParameters } from '@packages/shared/src/utils/mergeQueryParameters';
import { GlycerinPlacement, type GlycerinAdClickPayload } from '@packages/tracking';
import type { CriteoProduct } from '../../api/rest/types';
import { CriteoBeaconImage } from '../CriteoBeaconImage';
import { priceLabelMessages } from '../SearchProductCard/useVariationDataForProductCard';

export type CriteoProductCardProps = {
  product: CriteoProduct;
};

const sponsoredFlagText = defineMessage({
  id: 'search.productList.sponsored.flag',
  defaultMessage: 'Gesponsored',
});

// TODO: make a common function in utils to handle card props for visible variation
// here and also in 'components/SearchProductCard/SearchProductCard.tsx:L162'
const transformCriteoProductData = (
  product: CriteoProduct,
  query: ReturnType<typeof useRouter>['query'],
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
): ProductCardProps => {
  const variation = product.variations[0];

  // remove 'categories' and 'searchquery' from query object to avoid passing them to URL
  const { categories, searchquery, ...filteredQuery } = query;

  const productUrl = mergeQueryParameters(product.criteo.productUrl, filteredQuery);

  const uvpLabelString =
    (variation.oldPrice && formatMessage(priceLabelMessages.oldPrice)) || undefined;

  const clickTrackingProps: GlycerinAdClickPayload = {
    adId: 'criteo',
    adType: 'criteo',
    category: 'criteo-card-click',
    target: productUrl,
    label: `${product.brand.name} ${product.nameNoBrand}`,
    placement: GlycerinPlacement.LISTING,
  };

  return {
    sku: variation.sku,
    brand: product.brand.name,
    title: product.nameNoBrand,
    colors: [],
    availability: variation.availability,
    variationName: variation.variationName,
    productLink: productUrl,
    image: variation.imageUrl,
    additionalInfo: product.sellingPoints,
    price: {
      uvpLabel: uvpLabelString,
      currency: variation.price?.currency,
      price: variation.price.value * 100,
      oldPrice: variation.oldPrice?.value ? variation.oldPrice.value * 100 : undefined,
      discountType: variation.price.savingType?.toLowerCase() as 'currency' | 'percent' | undefined,
      discountInfoLabel: '-',
      isStartPrice: variation.isStartPrice,
      unit: variation.pricePerUnit?.unit,
      unitPrice: variation.pricePerUnit?.value ? variation.pricePerUnit.value * 100 : undefined,
      saving: variation.price.saving,
    },
    clickTrackingProps,
  };
};

/**
 * ProductCard component used to render sponsored criteo products
 * */
export const CriteoProductCard: FC<CriteoProductCardProps> = ({ product }) => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  return (
    <>
      {product.criteo.OnLoadBeacon && <CriteoBeaconImage src={product.criteo.OnLoadBeacon} />}
      {product.criteo.OnViewBeacon && <CriteoBeaconImage src={product.criteo.OnViewBeacon} />}
      <ProductCard
        onProductLinkActionAreaClick={setScrollingPosition}
        {...transformCriteoProductData(product, router.query, formatMessage)}
        flags={[{ text: formatMessage(sponsoredFlagText), type: 'neutral' }]}
        isSponsored
        priority
        shrinkToFit
      />
    </>
  );
};
